import WrapperContainer from "@components/WrapperContainer";
import ContentContainer from "@src/components/ContentContainer";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import PlaylistSongsList from "@src/components/PlaylistSongsList";
import { useNavigate, useParams } from "react-router-dom";
import { useNotification } from "@src/context/NotificationContext";
import { useFileUpload } from "@src/context/FileUploadContext";
import Spinner from "@src/components/Spinner";
import { onUpdateTrackParams } from "@src/components/PlaylistSongsList/PlaylistSongsList";
import {
  useUpdateTrackMutation,
  useFetchTrackAudioQuery,
} from "@src/queries/trackQueries";
import { usePlaylistDetails, usePlaylistTracks } from "@src/hooks/usePlaylists";
import { fetchPlaylistPeaksApi } from "@src/apis/playlistApis";
import { ITrack } from "@src/types/track";
import {
  usePlaylistTracksOrderAction,
  usePlaylistUpdateAction,
  useSubmitPlaylistMutation,
  useResetProcessedPlaylist,
  useUpdatePlaylistTagsMutation,
  useUpdatePlaylistRecommendationTagsMutation,
} from "@src/queries/playlistQueries";
import { AudioWaveform } from "@src/components/MusicPlayer";
import { useAppDispatch, useAppSelector } from "@src/redux/hooks";
import { AudioWaveformRef } from "@src/components/MusicPlayer/AudioWaveform";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { useForm } from "react-hook-form";
import {
  setCurrentPlayingProcessedPlaylist,
  setCurrentPlayingTrack,
  setIsPlaying,
  setTrackStatus,
} from "@src/redux/slices/trackPlayerSlice";
import { IPlaylistRecord, PlaylistActionType } from "@src/types/playlist";
import FileDropWrapper from "@src/components/FileDropzone/FileDropWrapper";
import PlaylistDetailsTopBar from "./PlaylistDetailsTopBar";
import PlaylistDetailsBottomBar from "./PlaylistDetailsBottomBar";
import PlaylistWaveForm from "./PlaylistWaveForm";
import { acceptAudioFiles } from "@src/utils/constants";
import { FEATURES } from "@src/constants";
import {
  useHasVisibleJinglesQuery,
  useShowJingleQuery,
} from "@src/queries/jingleQueries";
import ProcessingBadge from "@src/components/ProcessingBadge";
import { usePublicS3Downloader } from "@src/hooks/useS3Library";
import { usePrevious } from "@src/hooks/usePrevious";
import debounce from "lodash.debounce";
import { resetRemoveTrackFromPlaylist } from "@src/redux/slices/playlistDetailsSlice";
import ConfirmSubmitModal from "./ConfirmSubmit";
import useModal from "@src/hooks/useModal";
import AddExistingTracksDrawer from "@src/components/AddExistingTracksDrawer/AddExistingTracksDrawer";
import UpdatePlaylistTags from "./UpdatePlaylistTags";
import UpdatePlaylistRecommendationTags from "./UpdatePlaylistRecommendationTags";
import { useRole } from "@src/hooks/useAuth";
import { useMoodFactoryDetails } from "@src/queries/moodQueries";
import { formatMMSS } from "@src/utils/time";
import SelectJingleModal from "./SelectJingleModal";
import { useUpdatePlaylistJingleMutation } from "@src/queries/playlistQueries";
import UIModal from "@src/components/UIModal/UIModal";
import { UIButton } from "@src/components/UIButton";

const schema = yup.object().shape({
  searchText: yup
    .string()
    .max(150, "Search text must be less than 150 characters"),
});

const durationSeconds = 2 * 60 * 60; // 2 hours
const peaksPerSecond = 10; // change this for resolution

// pxPerSec zoom levels: 0 = fit full mix, up to 20 = ~1 min view at 1200px width
const ZOOM_LEVELS = [0, 0.5, 1, 3, 10, 20];

const generateFakePeaks = (length: number) => {
  const peaks = new Float32Array(length);
  for (let i = 0; i < length; i++) {
    // Create some variation with sine waves and random noise
    const sine = Math.abs(Math.sin(i / 100)) * 0.5;
    const noise = Math.random() * 0.5;
    peaks[i] = Math.min(1, sine + noise); // Clamp to 1
  }
  return peaks;
};

/**
 * Strapi stores playlistPeaks as a JSON field, so the raw API response can be:
 *  - A flat array: number[]
 *  - Already wrapped: { data: number[][] }
 *  - null / undefined
 * This helper normalises any of those shapes into { data: (Float32Array | number[])[] }
 * that AudioWaveform expects.
 */
const normalizePlaylistPeaks = (
  raw: unknown
): { data: (Float32Array | number[])[] } | null => {
  if (!raw) return null;
  // Already wrapped
  if (
    typeof raw === "object" &&
    !Array.isArray(raw) &&
    "data" in (raw as object)
  ) {
    const wrapped = raw as { data: unknown };
    if (Array.isArray(wrapped.data) && wrapped.data.length > 0) {
      return raw as { data: (Float32Array | number[])[] };
    }
    return null;
  }
  // Flat array of numbers → wrap in a single channel
  if (Array.isArray(raw) && raw.length > 0) {
    return { data: [raw as number[]] };
  }
  return null;
};

const PlaylistDetails = ({
  isMoodFactory = false,
}: {
  isMoodFactory?: boolean;
}): React.JSX.Element => {
  const role = useRole();
  const refetchTimeout = useRef<NodeJS.Timeout | null>(null);
  const { setValue, watch, reset } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      searchText: "",
    },
  });
  const [playlistDurationInSeconds, setPlaylistDurationInSeconds] =
    useState<number>(0);
  const routeParams = useParams();
  const playlistId = useMemo(
    () => routeParams.playlistId ?? "",
    [routeParams.playlistId]
  );
  const moodFactoryId = useMemo(
    () => routeParams?.moodFactoryId ?? "",
    [routeParams?.moodFactoryId]
  );

  const [processingBadgeStatus, setProcessingBadgeStatus] = useState({
    text: "Processing",
    secondaryText: "mix",
    show: false,
    success: false,
    error: true,
  });
  const [isAddExistingTracksDrawerOpen, setIsAddExistingTracksDrawerOpen] =
    useState<boolean>(false);
  const [searchTextValue, setSearchTextValue] = useState<string>("");
  const {
    isLoading,
    tracks: playlistTracks,
    refetch: refetchTracks,
  } = playlistId
    ? usePlaylistTracks(playlistId, { searchText: searchTextValue ?? "" })
    : { tracks: [] as ITrack[], isLoading: false, refetch: (): void => {} };
  const {
    playlist: rawPlaylistData,
    refetch: getPlaylistDetails,
    error: errorFetchingPlaylistDetails,
    isLoading: isLoadingPlaylistDetails,
  } = playlistId
    ? usePlaylistDetails(playlistId)
    : { playlist: null, refetch: (): void => {}, error: null, isLoading: true };

  const playlistData = useMemo(() => {
    if (!rawPlaylistData) return null;
    return {
      ...rawPlaylistData,
      playlistProcessedAudioDuration:
        rawPlaylistData.playlistProcessedAudioDuration ||
        playlistDurationInSeconds,
    };
  }, [rawPlaylistData, playlistDurationInSeconds]);

  const hasPlaylistAction = useMemo(() => {
    return (
      playlistData?.playlistAction &&
      (playlistData?.playlistAction === "cut" ||
        playlistData?.playlistAction === "crossfade")
    );
  }, [playlistData?.playlistAction]);
  const prevPlaylistData = usePrevious<IPlaylistRecord>(playlistData);
  const [isPlaylistDurationSufficient, setIsPlaylistDurationSufficient] =
    useState<boolean>(false);
  const dispatch = useAppDispatch();
  const { removeTrackFromPlaylistStatus, trackToRemove } = useAppSelector(
    (state) => state.playlistDetails
  );
  const [playlistTracksDisplaying, setPlaylistTracksDisplaying] = useState<
    ITrack[]
  >([]);
  const isPlaylistTracksValid = useMemo(() => {
    if (!playlistTracks || playlistTracks.length === 0) return false;
    return playlistTracks.every(
      (track) => (track.title ?? "").trim() && (track.artist ?? "").trim()
    );
  }, [playlistTracks]);

  const [elapsed, setElapsed] = useState(0);
  const [total, setTotal] = useState(0);
  const [zoomLevelIndex, setZoomLevelIndex] = useState(0);

  const { data: moodFactoryDetails } = useMoodFactoryDetails(
    moodFactoryId,
    role
  );

  const {
    currentPlayingTrack: trackSelected,
    currentPlayingProcessedPlaylist,
    trackStatus,
    trackURL,
    isPlaying,
  } = useAppSelector((state) => state.trackPlayer);

  const { data: trackAudioData } = useFetchTrackAudioQuery(
    trackSelected?.documentId
  );

  const { addNotification } = useNotification();
  const navigate = useNavigate();

  const {
    startUpload,
    progress,
    setError,
    uploading,
    setProgress,
    error: uploadError,
    filesCompletedCount,
    files,
  } = useFileUpload();

  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const handleFileInputChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (event.target.files && event.target.files.length > 0) {
      startUpload({
        newFiles: event.target.files,
        playlistId,
        curatorId: playlistData?.show?.curators?.[0]?.documentId ?? "",
      });
    }
  };

  const handleOnSelectSingleRow = (row: ITrack): void => {
    if (row.documentId !== trackSelected?.documentId) {
      if (waveformRef.current) {
        waveformRef.current.resetWaveform();
      }
    }
    dispatch(setCurrentPlayingTrack(row));
  };

  useEffect(() => {
    if (
      errorFetchingPlaylistDetails &&
      errorFetchingPlaylistDetails.response?.status === 404
    ) {
      addNotification("Playlist not found", "error");
      if (isMoodFactory) {
        return navigate("/mood-factory");
      }
      navigate("/playlists");
    }
  }, [errorFetchingPlaylistDetails]);
  useEffect(() => {
    if (uploadError) {
      addNotification(uploadError, "error");
      setProgress(0);
      setError("");
      return;
    }
    if (progress === 100 && !uploading) {
      getPlaylistDetails();
      refetchTracks();
      setProgress(0);
    }
    return () => {
      setProgress(0);
      dispatch(setCurrentPlayingTrack(null));
      dispatch(setIsPlaying(false));
      dispatch(setCurrentPlayingProcessedPlaylist(null));
    };
  }, [uploading]);
  useEffect(() => {
    dispatch(setCurrentPlayingTrack(null));
    dispatch(resetRemoveTrackFromPlaylist());
    dispatch(setIsPlaying(false));
    dispatch(setCurrentPlayingProcessedPlaylist(null));
  }, []);

  const refetchDataUntilAllTracksReady = () => {
    const allTracksReady = playlistTracks?.every((track: ITrack) => {
      return !!track.audio;
    });

    if (!allTracksReady) {
      refetchTimeout.current = setTimeout(() => {
        refetchTracks();
        refetchDataUntilAllTracksReady();
      }, 10000);
    }
  };

  useEffect(() => {
    if (playlistTracks.length) {
      refetchDataUntilAllTracksReady();
    }
    return () => {
      if (refetchTimeout.current) {
        clearTimeout(refetchTimeout.current);
      }
    };
  }, [playlistTracks]);

  const handlePlaylistAction = useCallback(
    async (action: PlaylistActionType) => {
      if (!playlistId) return;
      await updatePlaylistAction(playlistId, action);
      getPlaylistDetails();
    },
    [playlistId, isPlaylistTracksValid, addNotification]
  );
  const updateTracksOrderMutations = usePlaylistTracksOrderAction();
  const handleUpdateListOrder = (updateData: any): any => {
    if (updateData?.length === 0 || !playlistId) return;
    const tracksIds = updateData.map((track: any) => track.documentId);

    updateTracksOrderMutations.mutate(
      {
        playlistId: playlistId,
        tracksIds: tracksIds,
      },
      {
        onSuccess: () => {
          refetchTracks();
          if (
            playlistData?.playlistProcessedAudio ||
            playlistData?.stitchedTracksMetadata
          ) {
            resetProcessedPlaylist.mutate(
              { playlistId },
              {
                onSuccess: () => {
                  getPlaylistDetails();
                  addNotification(
                    "Mix invalidated due to track reordering",
                    "success"
                  );
                },
              }
            );
          }
        },
        onError: () => {
          addNotification("Failed to update playlist order", "error");
        },
      }
    );
  };
  const updatePlaylistMutation = usePlaylistUpdateAction();
  const resetProcessedPlaylist = useResetProcessedPlaylist();
  const submitPlaylist = useSubmitPlaylistMutation();
  const updatePlaylistTags = useUpdatePlaylistTagsMutation();
  const updatePlaylistRecommendationTags =
    useUpdatePlaylistRecommendationTagsMutation();
  const updatePlaylistJingle = useUpdatePlaylistJingleMutation();
  const updateIntervalRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (!playlistData?.playlistProcessedAudio?.url && hasPlaylistAction) {
      if (updateIntervalRef.current) clearInterval(updateIntervalRef.current);
      updateIntervalRef.current = setInterval(() => {
        recursivelyUpdatePlaylistData();
      }, 20000);
    } else {
      if (updateIntervalRef.current) {
        clearInterval(updateIntervalRef.current);
      }
      updateIntervalRef.current = null;
    }
    return () => {
      if (updateIntervalRef.current) {
        clearInterval(updateIntervalRef.current);
      }
      updateIntervalRef.current = null;
    };
  }, [playlistData?.playlistProcessedAudio?.url, playlistData?.playlistAction]);

  const recursivelyUpdatePlaylistData = async (): Promise<void> => {
    if (!playlistData?.playlistProcessedAudio?.url && hasPlaylistAction) {
      await getPlaylistDetails();
    }
  };

  const updatePlaylistAction = async (
    playlistId: string,
    action: PlaylistActionType
  ): Promise<void> => {
    if (!playlistId) return Promise.resolve();
    updatePlaylistMutation.mutate(
      {
        playlistId,
        action,
      },
      {
        onError: () => {
          addNotification("Failed to update playlist action", "error");
        },
        onSuccess: () => {
          getPlaylistDetails();
          refetchTracks();
          recursivelyUpdatePlaylistData();
          addNotification("Playlist action updated successfully", "success");
        },
      }
    );
  };

  useEffect(() => {
    if (removeTrackFromPlaylistStatus === "success") {
      if (trackToRemove?.documentId === trackSelected?.documentId) {
        dispatch(setCurrentPlayingTrack(null));
        dispatch(setIsPlaying(false));
        dispatch(setTrackStatus("idle"));
      }
      refetchTracks();
      if (
        playlistData?.playlistProcessedAudio ||
        playlistData?.stitchedTracksMetadata
      ) {
        resetProcessedPlaylist.mutate(
          { playlistId },
          {
            onSuccess: () => {
              getPlaylistDetails();
              addNotification(
                "Mix invalidated due to track removal",
                "success"
              );
            },
          }
        );
      }
    }
    return () => {
      reset();
    };
  }, [removeTrackFromPlaylistStatus]);
  useEffect(() => {
    if (
      removeTrackFromPlaylistStatus === "success" &&
      trackToRemove?.documentId
    ) {
      setPlaylistTracksDisplaying((prevTracks) =>
        prevTracks.filter(
          (track) => track.documentId !== String(trackToRemove.documentId)
        )
      );
    }
  }, [removeTrackFromPlaylistStatus, trackToRemove]);

  useEffect(() => {
    if (playlistTracks?.length) {
      let tracksToDisplay = [...playlistTracks];
      if (
        playlistData?.stitchedTracksMetadata?.tracks_cues &&
        Array.isArray(playlistData.stitchedTracksMetadata.tracks_cues) &&
        playlistData.stitchedTracksMetadata.tracks_cues.length > 0
      ) {
        const cues = playlistData.stitchedTracksMetadata.tracks_cues;
        const reorderedTracks: ITrack[] = [];
        const usedIndices = new Set<number>();

        cues.forEach((cue) => {
          if (
            cue?.track_index !== undefined &&
            cue.track_index >= 0 &&
            cue.track_index < playlistTracks.length
          ) {
            reorderedTracks.push(playlistTracks[cue.track_index]);
            usedIndices.add(cue.track_index);
          }
        });

        // Add any remaining tracks that might not be in tracks_cues
        playlistTracks.forEach((track, index) => {
          if (!usedIndices.has(index)) {
            reorderedTracks.push({ ...track, isExcludedFromMix: true });
          }
        });

        tracksToDisplay = reorderedTracks;
      } else {
        // Fallback: Estimate excluded tracks based on 2:04:00 limit (7440 seconds)
        let cumulative = 0;
        const MAX_DURATION = 7440;
        tracksToDisplay = tracksToDisplay.map((track) => {
          const isExcluded = cumulative >= MAX_DURATION;
          cumulative += track.duration ?? 0;
          return isExcluded ? { ...track, isExcludedFromMix: true } : track;
        });
      }
      setPlaylistTracksDisplaying(tracksToDisplay);
    }
  }, [playlistTracks, playlistData?.stitchedTracksMetadata]);
  const updateTrackMutation = useUpdateTrackMutation();
  const handleUpdateRow = async (
    updateData: onUpdateTrackParams
  ): Promise<void> => {
    if (!updateData?.id || !updateData?.field) return;
    const isRequiredTrackField =
      updateData.field === "title" || updateData.field === "artist";
    // Managers cannot edit track information, except a mood track's title and artist
    if (role === "manager" && !(isMoodFactory && isRequiredTrackField)) return;
    const normalizedValue = (updateData.value ?? "").trim();
    if (isRequiredTrackField && !normalizedValue) {
      addNotification("Track Title and Artist cannot be empty.", "error");
      return;
    }
    const previousTrack = playlistTracksDisplaying.find(
      (track) => track.documentId === updateData.id
    );
    const previousValue =
      (previousTrack?.[updateData.field as keyof ITrack] as
        | string
        | undefined
        | null) ?? "";

    setPlaylistTracksDisplaying((prevTracks) =>
      prevTracks.map((track) =>
        track.documentId === updateData.id
          ? ({
              ...track,
              [updateData.field]: normalizedValue,
            } as ITrack)
          : track
      )
    );

    updateTrackMutation.mutate(
      {
        trackData: {
          [updateData.field]: normalizedValue,
        },
        trackId: updateData.id,
      },
      {
        onError: () => {
          setPlaylistTracksDisplaying((prevTracks) =>
            prevTracks.map((track) =>
              track.documentId === updateData.id
                ? ({
                    ...track,
                    [updateData.field]: previousValue,
                  } as ITrack)
                : track
            )
          );
          addNotification("Failed to update track", "error");
        },
        onSuccess() {
          addNotification("Track updated successfully", "success");
          // Reconcile with server in background without blocking UI updates.
          refetchTracks();
        },
      }
    );
  };
  const waveformRef = useRef<AudioWaveformRef>(null);
  const currentZoomRef = useRef(0);
  const zoomAnimationRef = useRef<number | null>(null);
  const autoScrollPauseTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const pauseAutoScrollTemporarily = useCallback(() => {
    waveformRef.current?.setAutoScroll(false);
    if (autoScrollPauseTimeoutRef.current) {
      clearTimeout(autoScrollPauseTimeoutRef.current);
    }
    autoScrollPauseTimeoutRef.current = setTimeout(() => {
      waveformRef.current?.setAutoScroll(true);
      waveformRef.current?.scrollToCurrentTime();
      autoScrollPauseTimeoutRef.current = null;
    }, 3000);
  }, []);

  const smoothZoomTo = useCallback((from: number, to: number) => {
    if (zoomAnimationRef.current !== null) {
      cancelAnimationFrame(zoomAnimationRef.current);
    }
    const duration = 350;
    const startTime = performance.now();
    const animate = (now: number) => {
      const t = Math.min((now - startTime) / duration, 1);
      // ease-in-out cubic
      const eased = t < 0.5 ? 4 * t * t * t : 1 - (-2 * t + 2) ** 3 / 2;
      const value = from + (to - from) * eased;
      currentZoomRef.current = value;
      waveformRef.current?.zoom(value);
      if (t < 1) {
        zoomAnimationRef.current = requestAnimationFrame(animate);
      } else {
        zoomAnimationRef.current = null;
      }
    };
    zoomAnimationRef.current = requestAnimationFrame(animate);
  }, []);
  const handlePlayButtonClick = useCallback(
    (row: ITrack): void => {
      if (row.documentId === trackSelected?.documentId) {
        if (trackStatus === "loading") return;
        if (waveformRef.current?.isPlaying() || trackStatus === "playing") {
          return waveformRef.current?.pauseAudio();
        }
        if (!waveformRef.current?.isPlaying() || trackStatus === "paused") {
          return waveformRef.current?.playAudio();
        }
      }
      dispatch(setCurrentPlayingTrack(row));
      dispatch(setIsPlaying(true));
    },
    [dispatch, isPlaying, trackSelected?.documentId, trackStatus]
  );
  const handleZoomIn = useCallback(() => {
    const nextIndex = Math.min(zoomLevelIndex + 1, ZOOM_LEVELS.length - 1);
    setZoomLevelIndex(nextIndex);
    pauseAutoScrollTemporarily();
    smoothZoomTo(currentZoomRef.current, ZOOM_LEVELS[nextIndex]);
  }, [zoomLevelIndex, smoothZoomTo, pauseAutoScrollTemporarily]);

  const handleZoomOut = useCallback(() => {
    const prevIndex = Math.max(zoomLevelIndex - 1, 0);
    setZoomLevelIndex(prevIndex);
    pauseAutoScrollTemporarily();
    smoothZoomTo(currentZoomRef.current, ZOOM_LEVELS[prevIndex]);
  }, [zoomLevelIndex, smoothZoomTo, pauseAutoScrollTemporarily]);

  const handlePlayPlaylistProcessedAudio = async (): Promise<void> => {
    if (playlistData?.playlistProcessedAudio?.url === trackURL) {
      waveformRef.current?.toggleAudio();
      return;
    }
    if (playlistData?.playlistProcessedAudio?.url) {
      if (zoomAnimationRef.current !== null) {
        cancelAnimationFrame(zoomAnimationRef.current);
        zoomAnimationRef.current = null;
      }
      if (autoScrollPauseTimeoutRef.current) {
        clearTimeout(autoScrollPauseTimeoutRef.current);
        autoScrollPauseTimeoutRef.current = null;
      }
      currentZoomRef.current = 0;
      setZoomLevelIndex(0);
      dispatch(setCurrentPlayingTrack(null));

      try {
        const response = await fetchPlaylistPeaksApi(playlistId!);
        const rawPeaks = response.data.data.playlistPeaks;
        const playlistWithPeaks = {
          ...playlistData,
          playlistPeaks: normalizePlaylistPeaks(rawPeaks),
          // always carry the waveform assets from the latest playlistData
          waveform: playlistData.waveform ?? null,
          waveformBinary: playlistData.waveformBinary ?? null,
        };
        dispatch(setCurrentPlayingProcessedPlaylist(playlistWithPeaks));
      } catch {
        dispatch(setCurrentPlayingProcessedPlaylist(playlistData));
      }

      dispatch(setTrackStatus("playing"));
      dispatch(setIsPlaying(true));
    }
  };

  const activeTrackInMix = useMemo(() => {
    if (!currentPlayingProcessedPlaylist || !playlistTracks.length) return null;

    if (currentPlayingProcessedPlaylist.stitchedTracksMetadata?.tracks_cues) {
      const cues =
        currentPlayingProcessedPlaylist.stitchedTracksMetadata.tracks_cues;
      if (Array.isArray(cues) && cues.length > 0) {
        let activeCue = cues[0];
        for (const cue of cues) {
          if (cue?.start !== undefined && elapsed >= cue.start) {
            activeCue = cue;
          }
        }
        if (
          activeCue &&
          activeCue.track_index !== undefined &&
          activeCue.track_index >= 0 &&
          activeCue.track_index < playlistTracks.length
        ) {
          return playlistTracks[activeCue.track_index];
        }
      }
    }

    let cumulative = 0;
    for (const track of playlistTracksDisplaying) {
      cumulative += track.duration ?? 0;
      if (elapsed <= cumulative) return track;
    }
    return playlistTracksDisplaying[playlistTracksDisplaying.length - 1];
  }, [
    currentPlayingProcessedPlaylist,
    elapsed,
    playlistTracks,
    playlistTracksDisplaying,
  ]);
  useEffect(() => {
    return (): void => {
      dispatch(setCurrentPlayingTrack(null));
      dispatch(setIsPlaying(false));
      if (zoomAnimationRef.current !== null) {
        cancelAnimationFrame(zoomAnimationRef.current);
      }
      if (autoScrollPauseTimeoutRef.current) {
        clearTimeout(autoScrollPauseTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (isLoadingPlaylistDetails) {
      setProcessingBadgeStatus({
        text: "Loading",
        secondaryText: "mix",
        show: false,
        success: false,
        error: false,
      });
      return;
    }

    // 1. Error state: if there is a processingErrorMessage the process failed
    if (playlistData?.processingErrorMessage) {
      setProcessingBadgeStatus({
        text: "Error:",
        secondaryText: playlistData.processingErrorMessage,
        show: true,
        success: false,
        error: true,
      });
      return;
    }

    // Check if a process is ongoing or in queue
    // Logic: playlistStatus = ready AND playlistAction = cut | crossfade AND playlistProcessedAudio = null
    const isProcessingOrInQueue =
      !playlistData?.playlistProcessedAudio?.url &&
      (playlistData?.playlistProcessing ||
        (playlistData?.playlistStatus === "ready" && hasPlaylistAction));

    if (isProcessingOrInQueue) {
      recursivelyUpdatePlaylistData();
      setProcessingBadgeStatus({
        text: "Processing",
        secondaryText: "mix",
        show: true,
        success: false,
        error: false,
      });
    } else if (playlistData?.playlistProcessedAudio?.url) {
      // 2. Completed state: when playlistProcessedAudio is valid playlist process finished
      // We show "Completed" briefly if it just finished, otherwise we keep the play/download options available elsewhere
      if (
        prevPlaylistData &&
        !prevPlaylistData?.playlistProcessedAudio?.url &&
        hasPlaylistAction
      ) {
        setProcessingBadgeStatus({
          text: "Completed",
          secondaryText: "",
          show: true,
          success: true,
          error: false,
        });
        setTimeout(() => {
          setProcessingBadgeStatus((prev) => ({ ...prev, show: false }));
        }, 3000);
      } else {
        setProcessingBadgeStatus({
          text: "Processing",
          secondaryText: "mix",
          show: false,
          success: false,
          error: false,
        });
      }
    } else {
      setProcessingBadgeStatus({
        text: "Processing",
        secondaryText: "mix",
        show: false,
        success: false,
        error: false,
      });
    }
  }, [
    playlistData?.playlistProcessedAudio,
    playlistData?.playlistAction,
    playlistData?.playlistStatus,
    playlistData?.processingErrorMessage,
    playlistData?.playlistProcessing,
    isLoadingPlaylistDetails,
  ]);
  const {
    isDownloading,
    downloadPlaylistById,
    error: errorDownloading,
  } = usePublicS3Downloader();
  useEffect(() => {
    if (errorDownloading) {
      addNotification(errorDownloading, "error");
    }
  }, [errorDownloading]);
  useEffect(() => {
    const totalDuration =
      playlistData?.tracksDuration ||
      (playlistTracks?.reduce((acc, track) => {
        return acc + (track.duration ?? 0);
      }, 0) ??
        0);
    setPlaylistDurationInSeconds(totalDuration || 0);
    setIsPlaylistDurationSufficient(totalDuration >= 7440);
  }, [playlistTracks, playlistData?.tracksDuration]);

  let trackOrPlaylistPeaks: { data: (Float32Array | number[])[] } | undefined;
  let playlistWaveformImageUrl: string | undefined;
  let playlistWaveformBinaryUrl: string | undefined;

  if (currentPlayingProcessedPlaylist) {
    const waveformBinaryUrl = FEATURES.WAVEFORM_BINARY_ENABLED
      ? currentPlayingProcessedPlaylist.waveformBinary?.url
      : undefined;
    const normalizedPlaylistPeaks = normalizePlaylistPeaks(
      currentPlayingProcessedPlaylist.playlistPeaks
    );

    if (waveformBinaryUrl) {
      // Priority 1: compact binary waveform (~3 peaks/second at any length),
      // with the image passed along as its fallback if the download fails
      playlistWaveformBinaryUrl = waveformBinaryUrl;
      playlistWaveformImageUrl = currentPlayingProcessedPlaylist.waveform?.url;
    } else if (normalizedPlaylistPeaks?.data?.length) {
      // Priority 2: real playlistPeaks
      trackOrPlaylistPeaks = normalizedPlaylistPeaks;
    } else if (currentPlayingProcessedPlaylist.waveform?.url) {
      // Priority 3: pre-rendered waveform image from Strapi
      playlistWaveformImageUrl = currentPlayingProcessedPlaylist.waveform.url;
    } else {
      // Priority 4: fake peaks as last resort
      trackOrPlaylistPeaks = {
        data: [generateFakePeaks(durationSeconds * peaksPerSecond)],
      };
    }
  } else if (
    trackSelected?.trackPeaks &&
    trackSelected?.trackPeaks?.data?.length
  ) {
    trackOrPlaylistPeaks = trackSelected?.trackPeaks;
  }

  const handleUpdatePlaylistTags = async (
    selectedTags: string[],
    selectedSubtags: string[] | undefined
  ) => {
    await updatePlaylistTags.mutateAsync(
      {
        playlistId: playlistId,
        playlistTagsIds: selectedTags,
        playlistSubtagsIds: selectedSubtags,
      },
      {
        onError: () => {
          addNotification("Error updating playlist tags", "error");
        },
        onSuccess: () => {
          getPlaylistDetails();
          addNotification("Playlist tags updated successfully", "success");
          closeUpdateTagsModal();
        },
      }
    );
  };

  const handleUpdatePlaylistRecommendationTags = async (
    recommendationTagIds: string[]
  ) => {
    await updatePlaylistRecommendationTags.mutateAsync(
      {
        playlistId: playlistId,
        recommendationTagIds: recommendationTagIds,
      },
      {
        onError: () => {
          addNotification(
            "Error updating playlist recommendation tags",
            "error"
          );
        },
        onSuccess: () => {
          getPlaylistDetails();
          addNotification(
            "Playlist recommendation tags updated successfully",
            "success"
          );
          closeUpdateRecommendationTagsModal();
        },
      }
    );
  };

  const debouncedSearch = useMemo(
    () =>
      debounce((value: string) => {
        setSearchTextValue(value);
      }, 500),
    []
  );
  const handleSearchChange = (value: string) => {
    setValue("searchText", value);
    debouncedSearch(value);
  };
  const {
    closeModal: confirmSubmissionCloseModal,
    isOpen: confirmSubmissionIsOpen,
    openModal: confirmSubmissionOpenModal,
  } = useModal();

  const handleSubmitPlaylist = async (
    selectedPlaylistTags: string[],
    selectedPlaylistSubtags: string[] | undefined
  ) => {
    if (!isPlaylistDurationSufficient) {
      addNotification(
        "Playlist duration must be at least 2 hours and 4 minutes",
        "error"
      );
      return;
    }
    if (!isPlaylistTracksValid) {
      addNotification(
        "Filling in the Title and Artist for every track is required before submission.",
        "error"
      );
      return;
    }
    try {
      await submitPlaylist.mutateAsync({
        playlistId,
        playlistTagsIds: selectedPlaylistTags,
        playlistSubtagsIds: selectedPlaylistSubtags,
      });
      getPlaylistDetails();
      addNotification("Playlist submitted successfully", "success");
      confirmSubmissionCloseModal();
    } catch {
      addNotification("Failed to submit playlist", "error");
      confirmSubmissionCloseModal();
    }
  };

  const {
    closeModal: closeUpdateTagsModal,
    isOpen: updateTagsIsOpen,
    openModal: openUpdateTagsModal,
  } = useModal();
  const {
    closeModal: closeUpdateRecommendationTagsModal,
    isOpen: updateRecommendationTagsIsOpen,
    openModal: openUpdateRecommendationTagsModal,
  } = useModal();

  const {
    closeModal: closeJingleModal,
    isOpen: jingleModalIsOpen,
    openModal: openJingleModal,
  } = useModal();
  // The playlist's show already supplies a fallback jingle, so its own jingle is
  // never offered as a manual pick here. [ROVR-4036]
  const { data: showJingleId, isLoading: loadingShowJingle } =
    useShowJingleQuery(
      playlistData?.show?.documentId,
      FEATURES.PLAYLIST_JINGLE
    );
  const { data: hasVisibleJingles } = useHasVisibleJinglesQuery(
    FEATURES.PLAYLIST_JINGLE && !loadingShowJingle,
    showJingleId
  );
  const {
    closeModal: closeMissingTrackFieldsModal,
    isOpen: missingTrackFieldsIsOpen,
    openModal: openMissingTrackFieldsModal,
  } = useModal();

  const handleUpdateJingle = async (
    jingleDocumentId: string | null
  ): Promise<void> => {
    await updatePlaylistJingle.mutateAsync(
      { playlistId, jingleDocumentId },
      {
        onError: () => {
          addNotification("Error updating jingle", "error");
        },
        onSuccess: () => {
          getPlaylistDetails();
          addNotification("Jingle updated successfully", "success");
          closeJingleModal();
        },
      }
    );
  };

  const onAddMoodFactory = useCallback(() => {
    setIsAddExistingTracksDrawerOpen(true);
  }, []);
  const onAddTrack = useCallback(() => {
    fileInputRef.current?.click();
  }, []);
  const onAdd = () => {
    if (isMoodFactory) {
      onAddMoodFactory();
    } else {
      onAddTrack();
    }
  };

  return (
    <FileDropWrapper
      disableDrag={
        hasPlaylistAction || isLoadingPlaylistDetails || confirmSubmissionIsOpen
      }
      onClick={() => waveformRef.current?.resetWaveform()}
      accept={acceptAudioFiles}
      multiple
      onDrop={(files) => {
        if (hasPlaylistAction) return;
        startUpload({
          newFiles: files,
          playlistId,
          curatorId: playlistData?.show?.curators?.[0].documentId ?? "",
        });
      }}
      uploading={uploading}
      progress={progress}
      filesCompletedCount={filesCompletedCount}
      filesCount={files?.length}
    >
      <WrapperContainer noScroll={true}>
        <PlaylistDetailsTopBar
          onClick={() => waveformRef.current?.resetWaveform()}
          playlistDetails={playlistData}
          isLoadingPlaylistDetails={isLoadingPlaylistDetails}
          isPlaylistDurationSufficient={isPlaylistDurationSufficient}
          currentPlaylistAction={playlistData?.playlistAction}
          isPlaylistTracksValid={isPlaylistTracksValid}
          currentDuration={formatDuration(playlistDurationInSeconds)}
          trackCount={playlistTracks?.length}
          status={playlistData?.playlistStatus}
          onReset={() => {
            resetProcessedPlaylist.mutate(
              { playlistId },
              {
                onSuccess: () => {
                  getPlaylistDetails();
                  addNotification("Playlist reset successfully", "success");
                },
                onError: (error: any) => {
                  addNotification(
                    error.response?.data?.error?.message ||
                      "Failed to reset playlist",
                    "error"
                  );
                },
              }
            );
          }}
          onCutMix={() => handlePlaylistAction("cut")}
          onAutoMix={() => handlePlaylistAction("crossfade")}
          isDownloadingPlaylist={isDownloading}
          onDownload={() => {
            if (isDownloading) return;

            if (playlistData?.playlistProcessedAudio?.url) {
              downloadPlaylistById(playlistData.documentId);
            }
          }}
          onPlay={() => handlePlayPlaylistProcessedAudio()}
          onSubmit={() => {
            if (!isPlaylistDurationSufficient) {
              addNotification(
                "Playlist duration must be at least 2 hours and 4 minutes",
                "error"
              );
              return;
            }
            if (!isPlaylistTracksValid) {
              openMissingTrackFieldsModal();
              return;
            }
            confirmSubmissionOpenModal();
          }}
        />
        <UpdatePlaylistTags
          isOpen={updateTagsIsOpen}
          onClose={closeUpdateTagsModal}
          onConfirm={handleUpdatePlaylistTags}
          key={playlistId}
          loading={updatePlaylistTags.isPending}
          playlistData={playlistData}
        />
        <UpdatePlaylistRecommendationTags
          isOpen={updateRecommendationTagsIsOpen}
          onClose={closeUpdateRecommendationTagsModal}
          onConfirm={handleUpdatePlaylistRecommendationTags}
          loading={updatePlaylistRecommendationTags.isPending}
          playlistData={playlistData}
          isLoadingPlaylistDetails={isLoadingPlaylistDetails}
        />
        {FEATURES.PLAYLIST_JINGLE && (
          <SelectJingleModal
            isOpen={jingleModalIsOpen}
            onClose={closeJingleModal}
            onConfirm={handleUpdateJingle}
            loading={updatePlaylistJingle.isPending}
            playlistData={playlistData}
            showJingleId={showJingleId}
          />
        )}
        <UIModal
          isOpen={missingTrackFieldsIsOpen}
          onClose={closeMissingTrackFieldsModal}
          title="Missing Track Information"
          headerStyles={{ textAlign: "center" }}
          hideCloseButton
          actions={
            <UIButton
              onClick={closeMissingTrackFieldsModal}
              variant="primary"
              style={{ minWidth: 120 }}
            >
              Ok
            </UIButton>
          }
          modalStyles={{ width: 340, borderRadius: 18, padding: "20px 24px" }}
        >
          <p style={{ margin: 0, lineHeight: 1.4, textAlign: "center" }}>
            Filling in the Title and Artist for every track is required before
            submission
          </p>
        </UIModal>
        <ConfirmSubmitModal
          loading={submitPlaylist.isPending}
          isOpen={confirmSubmissionIsOpen}
          onClose={confirmSubmissionCloseModal}
          onConfirm={handleSubmitPlaylist}
        />
        {trackSelected || currentPlayingProcessedPlaylist ? (
          <PlaylistWaveForm
            trackStatus={trackStatus}
            hideControls={
              !!currentPlayingProcessedPlaylist?.playlistProcessedAudio?.url
            }
            showZoomControls={
              !!currentPlayingProcessedPlaylist?.playlistProcessedAudio?.url
            }
            zoomLevelIndex={zoomLevelIndex}
            maxZoomLevelIndex={ZOOM_LEVELS.length - 1}
            onZoomIn={handleZoomIn}
            onZoomOut={handleZoomOut}
            onNextClick={() => {
              if (currentPlayingProcessedPlaylist?.playlistProcessedAudio?.url)
                return;
              if (trackSelected)
                handleOnSelectSingleRow(
                  playlistTracks[playlistTracks.indexOf(trackSelected) + 1]
                );
            }}
            onPreviousClick={() => {
              if (currentPlayingProcessedPlaylist?.playlistProcessedAudio?.url)
                return;
              if (trackSelected)
                handleOnSelectSingleRow(
                  playlistTracks[playlistTracks.indexOf(trackSelected) - 1]
                );
            }}
            isFirstTrackSelected={trackSelected === playlistTracks[0]}
            isLastTrackSelected={
              trackSelected === playlistTracks[playlistTracks.length - 1]
            }
            onPlayClick={() => waveformRef.current?.playAudio()}
            onPauseClick={() => waveformRef.current?.pauseAudio()}
            trackName={
              trackSelected?.title ||
              currentPlayingProcessedPlaylist?.title ||
              ""
            }
            elapsedTime={formatMMSS(elapsed)}
            totalTime={formatMMSS(total)}
          >
            {(trackSelected || currentPlayingProcessedPlaylist) && (
              <AudioWaveform
                startPlaying={isPlaying}
                ref={waveformRef}
                peaks={
                  trackOrPlaylistPeaks?.data ??
                  (trackAudioData as any)?.data?.trackPeaks?.data
                }
                waveformImageUrl={playlistWaveformImageUrl}
                waveformBinaryUrl={playlistWaveformBinaryUrl}
                trackURL={trackURL ?? ""}
                onReady={(d) => setTotal(Math.floor(d || 0))}
                onTimeUpdate={(cur, d) => {
                  setElapsed(Math.floor(cur || 0));
                  if (d) setTotal(Math.floor(d));
                }}
                onUserScroll={
                  currentPlayingProcessedPlaylist
                    ? pauseAutoScrollTemporarily
                    : undefined
                }
              />
            )}
          </PlaylistWaveForm>
        ) : (
          <></>
        )}
        <PlaylistDetailsBottomBar
          isMoodFactory={isMoodFactory}
          onTagsClicked={
            role !== "admin" && role !== "manager"
              ? undefined
              : () => {
                  openUpdateTagsModal();
                }
          }
          onRecommendationTagsClicked={
            role === "admin" || role === "manager"
              ? () => {
                  openUpdateRecommendationTagsModal();
                }
              : undefined
          }
          onJingleClicked={
            FEATURES.PLAYLIST_JINGLE &&
            playlistData?.playlistStatus &&
            ["draft", "ready"].includes(playlistData.playlistStatus) &&
            // Hide the jingle row only when there's nothing to offer AND nothing
            // already assigned; an assigned jingle stays visible/usable.
            (hasVisibleJingles || !!playlistData?.jingle)
              ? () => openJingleModal()
              : undefined
          }
          onClick={() => waveformRef.current?.resetWaveform()}
          showTitle={
            isMoodFactory
              ? moodFactoryDetails?.title
              : (playlistData?.show?.title ?? "")
          }
          playlistTitle={playlistData?.title ?? ""}
          playlistDescription={playlistData?.overwriteShowDescription ?? ""}
          onBack={() => {
            if (isMoodFactory) {
              return navigate(`/mood-factory`);
            }
            return navigate("/playlists");
          }}
          onAdd={onAdd}
          onSearchChange={handleSearchChange}
          searchValue={watch("searchText")}
          playlistStatus={playlistData?.playlistStatus ?? "draft"}
          playlistData={playlistData}
        />
        <input
          type="file"
          ref={fileInputRef}
          style={{ display: "none" }}
          accept={acceptAudioFiles}
          multiple
          onChange={handleFileInputChange}
        />
        <ContentContainer allowScroll={true}>
          <AddExistingTracksDrawer
            handlePlayButtonClick={handlePlayButtonClick}
            addTrackCallback={() => refetchTracks()}
            playlistId={playlistId}
            isOpen={isAddExistingTracksDrawerOpen}
            toggleDrawer={() =>
              setIsAddExistingTracksDrawerOpen(!isAddExistingTracksDrawerOpen)
            }
            key={`add-existing-tracks-drawer-${playlistId}`}
          />
          <div
            style={{
              display: isLoading ? "flex" : "none",
              justifyContent: "center",
              alignItems: "center",
              width: "100vw",
              height: "50vh",
              position: "absolute",
            }}
          >
            {isLoading && <Spinner />}
          </div>
          <PlaylistSongsList
            playlistDetails={playlistData}
            playlistStatus={playlistData?.playlistStatus ?? "draft"}
            onRowClick={handleOnSelectSingleRow}
            onOrderChange={handleUpdateListOrder}
            onUpdateRow={handleUpdateRow}
            data={playlistTracksDisplaying ?? []}
            handlePlayButtonClick={handlePlayButtonClick}
            activeTrackInMix={activeTrackInMix}
            isMoodFactory={isMoodFactory}
          />
        </ContentContainer>

        {processingBadgeStatus.show === true ? (
          <ProcessingBadge
            success={processingBadgeStatus.success}
            text={processingBadgeStatus.text}
            secondaryText={processingBadgeStatus.secondaryText}
            showError={processingBadgeStatus.error}
          />
        ) : (
          <></>
        )}
      </WrapperContainer>
    </FileDropWrapper>
  );
};
const formatDuration = (seconds?: number): string => {
  if (!seconds) return "00:00:00";
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60)
    .toFixed(0)
    .padStart(2, "0");
  const remainingSeconds = Math.floor(seconds % 60)
    .toFixed(0)
    .padStart(2, "0");
  return `${hours}:${minutes}:${remainingSeconds}`;
};
export default PlaylistDetails;
